import React, { useEffect, useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { ResponsiveBar } from '@nivo/bar';
import AdminLayout from '../../components/layout/AdminLayout';
import { getPosSalesStats } from '../../api/posSales';
import './posSales.css';

// 차트 색상 - 디자인 토큰(현대카드 톤)과 맞춤. onetak=the Red, POS=블랙.
const C_ONETAK = '#da0011';
const C_POS = '#111111';

const num = (v) => Number(v || 0).toLocaleString();
const won = (v) => `${num(v)}원`;
const pad2 = (n) => String(n).padStart(2, '0');
const fmtDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// 축 눈금: 1만 미만은 원, 이상은 만 단위(필요 시 소수1자리)로 손실 없이 표기.
const manFmt = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) < 10000) return n.toLocaleString();
  const man = n / 10000;
  return `${Number.isInteger(man) ? man.toLocaleString() : man.toFixed(1)}만`;
};

const chartTooltip = (label, value) => (
  <div style={{ padding: '5px 9px', background: '#fff', border: '1px solid #e5e5e5', borderRadius: 3, fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
    <strong>{label}</strong><br />{Number(value).toLocaleString()}원
  </div>
);

// 프리셋 기간 계산 (모두 로컬=KST 기준)
function presetRange(key) {
  const today = new Date();
  const start = new Date(today);
  if (key === 'today') return [fmtDate(today), fmtDate(today)];
  if (key === '7d') { start.setDate(today.getDate() - 6); return [fmtDate(start), fmtDate(today)]; }
  if (key === 'month') { start.setDate(1); return [fmtDate(start), fmtDate(today)]; }
  if (key === 'lastMonth') {
    const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const last = new Date(today.getFullYear(), today.getMonth(), 0);
    return [fmtDate(first), fmtDate(last)];
  }
  return [fmtDate(today), fmtDate(today)];
}

/**
 * 기간별 매출 통계 - 임의 기간(from~to)의 매장별(onetak vs POS) + 상품별 매출을 조회하고,
 * 화면 데이터를 엑셀(.xlsx, 2시트)로 내려받는다.
 */
export default function PosSalesStatsPage() {
  const [from, setFrom] = useState(() => presetRange('7d')[0]);
  const [to, setTo] = useState(() => presetRange('7d')[1]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = useCallback(async (f, t) => {
    setLoading(true);
    setError('');
    try {
      const res = await getPosSalesStats(f, t);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || '매출 통계를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(from, to); }, [from, to, fetchStats]);

  const applyPreset = (key) => {
    const [f, t] = presetRange(key);
    setFrom(f);
    setTo(t);
  };

  const stores = data?.stores || [];
  const products = data?.products || [];
  const totalOnetak = data?.totalOnetakAmount || 0;
  const totalPos = data?.totalPosAmount || 0;
  const diff = totalOnetak - totalPos;
  const hasData = stores.length > 0 || products.length > 0;

  const storeChartData = stores.slice(0, 8).map((r) => ({
    store: r.storeName,
    onetak: Number(r.onetakAmount) || 0,
    POS: Number(r.posAmount) || 0,
  }));

  const handleExcel = () => {
    const storeRows = stores.map((r) => ({
      매장: r.storeName,
      'onetak 매출': Number(r.onetakAmount) || 0,
      'onetak 건수': Number(r.onetakCount) || 0,
      'POS 매출': Number(r.posAmount) || 0,
      'POS 건수': Number(r.posCount) || 0,
      '차액(onetak-POS)': (Number(r.onetakAmount) || 0) - (Number(r.posAmount) || 0),
    }));
    const productRows = products.map((p) => ({
      상품: p.productName,
      수량: Number(p.qty) || 0,
      매출: Number(p.amount) || 0,
      주문수: Number(p.orderCount) || 0,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(storeRows), '매장별');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(productRows), '상품별');
    XLSX.writeFile(wb, `매출통계_${from}_${to}.xlsx`);
  };

  return (
    <AdminLayout title="기간별매출통계" menuUrl="/admin/sales-stats">
      <div className="pos-page">
        <div className="page-header">
          <div>
            <h1>기간별 매출 통계</h1>
            <div className="subtitle">시작일~종료일 범위의 매장별·상품별 매출을 집계하고 엑셀로 내려받습니다.</div>
          </div>
        </div>

        <div className="card pos-card pos-toolbar">
          <div className="btn-group">
            <button type="button" className="btn btn-secondary" onClick={() => applyPreset('today')}>오늘</button>
            <button type="button" className="btn btn-secondary" onClick={() => applyPreset('7d')}>최근 7일</button>
            <button type="button" className="btn btn-secondary" onClick={() => applyPreset('month')}>이번달</button>
            <button type="button" className="btn btn-secondary" onClick={() => applyPreset('lastMonth')}>지난달</button>
          </div>
          <input className="input" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          <span style={{ color: 'var(--color-text-tertiary)' }}>~</span>
          <input className="input" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          <button type="button" className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={handleExcel} disabled={!hasData}>
            엑셀 다운로드
          </button>
        </div>

        <div className="pos-kpis">
          <div className="pos-kpi onetak">
            <div className="label">onetak 결제매출</div>
            <div className="value">{num(totalOnetak)}<span className="unit">원</span></div>
          </div>
          <div className="pos-kpi pos">
            <div className="label">POS 전송매출</div>
            <div className="value">{num(totalPos)}<span className="unit">원</span></div>
          </div>
          <div className="pos-kpi gap">
            <div className="label">미전송 차액 (onetak − POS)</div>
            <div className={`value ${diff > 0 ? 'pos-neg' : 'pos-ok'}`}>{num(diff)}<span className="unit">원</span></div>
            <div className="sub">기간: {from} ~ {to}</div>
          </div>
        </div>

        {/* 매장별 섹션 */}
        <div className="card pos-card">
          <div className="pos-section-title">매장별 매출 (onetak vs POS)</div>
          <div className="pos-legend">
            <span><i style={{ background: C_ONETAK }} />onetak 결제매출</span>
            <span><i style={{ background: C_POS }} />POS 전송매출</span>
          </div>
          <div className="pos-chart">
            {loading && <div className="empty-row">불러오는 중...</div>}
            {!loading && error && <div className="empty-row">{error}</div>}
            {!loading && !error && storeChartData.length === 0 && <div className="empty-row">매출 데이터가 없습니다.</div>}
            {!loading && !error && storeChartData.length > 0 && (
              <ResponsiveBar
                data={storeChartData}
                keys={['onetak', 'POS']}
                indexBy="store"
                groupMode="grouped"
                margin={{ top: 12, right: 16, bottom: 64, left: 64 }}
                padding={0.28}
                innerPadding={3}
                colors={[C_ONETAK, C_POS]}
                enableLabel={false}
                axisLeft={{ format: manFmt }}
                axisBottom={{ tickRotation: -22 }}
                valueFormat={(v) => `${Number(v).toLocaleString()}원`}
                theme={{ fontFamily: 'inherit', fontSize: 12, textColor: '#666' }}
                tooltip={({ id, indexValue, value }) => chartTooltip(`${indexValue} · ${id}`, value)}
                animate={false}
              />
            )}
          </div>
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>매장</th>
                  <th className="pos-num">onetak 매출</th>
                  <th className="pos-num">onetak 건수</th>
                  <th className="pos-num">POS 매출</th>
                  <th className="pos-num">POS 건수</th>
                  <th className="pos-num">차액</th>
                </tr>
              </thead>
              <tbody>
                {!loading && !error && stores.length === 0 && <tr><td colSpan={6} className="empty-row">데이터 없음</td></tr>}
                {!loading && !error && stores.map((r) => {
                  const d = (Number(r.onetakAmount) || 0) - (Number(r.posAmount) || 0);
                  return (
                    <tr key={r.storeId}>
                      <td style={{ fontWeight: 600 }}>{r.storeName}</td>
                      <td className="pos-num">{won(r.onetakAmount)}</td>
                      <td className="pos-num">{num(r.onetakCount)}</td>
                      <td className="pos-num pos-pos-val">{won(r.posAmount)}</td>
                      <td className="pos-num">{num(r.posCount)}</td>
                      <td className={`pos-num ${d > 0 ? 'pos-neg' : 'pos-ok'}`}>{won(d)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* 상품별 섹션 */}
        <div className="card pos-card">
          <div className="pos-section-title">상품별 매출 (전체 매장 합산, onetak 결제 기준)</div>
          <div className="pos-chart">
            {loading && <div className="empty-row">불러오는 중...</div>}
            {!loading && !error && products.length === 0 && <div className="empty-row">매출 데이터가 없습니다.</div>}
            {!loading && !error && products.length > 0 && (
              <ResponsiveBar
                data={products.slice(0, 10).map((p) => ({ product: p.productName, 매출: Number(p.amount) || 0 })).reverse()}
                keys={['매출']}
                indexBy="product"
                layout="horizontal"
                margin={{ top: 8, right: 24, bottom: 40, left: 140 }}
                padding={0.32}
                colors={[C_ONETAK]}
                enableGridX
                enableGridY={false}
                enableLabel={false}
                axisBottom={{ format: manFmt }}
                valueFormat={(v) => `${Number(v).toLocaleString()}원`}
                theme={{ fontFamily: 'inherit', fontSize: 12, textColor: '#666' }}
                tooltip={({ indexValue, value }) => chartTooltip(indexValue, value)}
                animate={false}
              />
            )}
          </div>
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>상품</th>
                  <th className="pos-num">수량</th>
                  <th className="pos-num">매출</th>
                  <th className="pos-num">주문수</th>
                </tr>
              </thead>
              <tbody>
                {!loading && !error && products.length === 0 && <tr><td colSpan={4} className="empty-row">데이터 없음</td></tr>}
                {!loading && !error && products.map((p) => (
                  <tr key={p.productId}>
                    <td style={{ fontWeight: 600 }}>{p.productName}</td>
                    <td className="pos-num">{num(p.qty)}</td>
                    <td className="pos-num pos-pos-val">{won(p.amount)}</td>
                    <td className="pos-num">{num(p.orderCount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
